import React, { Component } from 'react';
import { FiUsers, FiX, FiInfo, FiShare2, FiAlertCircle } from 'react-icons/fi';
import { SimpleDesignConfig, defaultDesignConfig } from '../SimpleDesignConfig';
import { env } from '../env';
import './DesignConfiguratorSA.css';

// Quell-Version (gespeicherte Version der Ausgangs-Gemeinde)
interface SAVersion {
    version: number;
    erstelltAm: string;
    geaendertAm: string;
    istLive?: boolean;
    konfigurationJson: string;
}

// Quell-Vorlage
interface SATemplate {
    id: number;
    name: string;
    konfigurationJson: string;
}

// Ziel-Kunde mit seinen Gemeinden
interface SAKunde {
    kunde: number;
    name: string;
    gemeinden: { gemeinde: number; name: string }[];
}

interface Props {
    // Quell-Gemeinde, mit der der Designer geöffnet wurde
    kunde: number;
    gemeinde: number;
    jahr: number;
    // Auth-Token ("Bearer ...") für die rollengeschützten Endpoints
    authToken: string;
    // Quellen: gespeicherte Versionen dieser Gemeinde sowie verfügbare Vorlagen
    versions: SAVersion[];
    templates: SATemplate[];
    onClose: () => void;
    onSuccess: (anzahl: number) => void;
}

interface State {
    allKunden: SAKunde[];
    isLoadingKunden: boolean;
    quelleTyp: 'version' | 'template' | null;
    quelleId: number | null;
    zielKunde: number | null;
    zielGemeinden: number[];
    isTransferring: boolean;
    error: string | null;
}

/**
 * Superadmin-Werkzeug: überträgt ein Design (gespeicherte Version oder Vorlage)
 * kundenübergreifend auf einen anderen Kunden und dessen Gemeinden.
 * Wird als Dialog aus dem DesignConfigurator geöffnet.
 */
export class DesignConfiguratorSA extends Component<Props, State> {

    constructor(props: Props) {
        super(props);
        this.state = {
            allKunden: [],
            isLoadingKunden: false,
            quelleTyp: null,
            quelleId: null,
            zielKunde: null,
            zielGemeinden: [],
            isTransferring: false,
            error: null
        };
    }

    componentDidMount() {
        this.loadAllKunden();
    }

    // Lädt alle Kunden mit ihren Gemeinden (für die Ziel-Auswahl)
    loadAllKunden = async () => {
        this.setState({ isLoadingKunden: true });
        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const response = await fetch(`${api}/api/Design/all-kunden`, {
                headers: { 'Authorization': this.props.authToken }
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.json();
            this.setState({ allKunden: data || [], isLoadingKunden: false });
        } catch (error) {
            this.setState({ allKunden: [], isLoadingKunden: false });
        }
    }

    selectQuelle = (typ: 'version' | 'template', id: number) => {
        this.setState({ quelleTyp: typ, quelleId: id });
    }

    // Ziel-Kunde wählen; die Gemeindenauswahl wird dabei zurückgesetzt
    selectZielKunde = (kunde: number | null) => {
        this.setState({ zielKunde: kunde, zielGemeinden: [] });
    }

    toggleZielGemeinde = (gemeindeId: number) => {
        this.setState(prev => {
            const sel = prev.zielGemeinden.includes(gemeindeId)
                ? prev.zielGemeinden.filter(id => id !== gemeindeId)
                : [...prev.zielGemeinden, gemeindeId];
            return { zielGemeinden: sel };
        });
    }

    // Formatiert einen ISO-Zeitstempel als deutsches Datum mit Uhrzeit
    formatTimestamp = (iso: string): string => {
        try {
            return new Date(iso).toLocaleString('de-DE', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        } catch {
            return '';
        }
    }

    // leichtgewichtiges Parsen der Quell-Konfiguration für die Mini-Vorschau
    parseConfig = (json: string): SimpleDesignConfig => {
        try {
            const p = JSON.parse(json);
            return {
                ...defaultDesignConfig,
                ...p,
                tabTop: { ...defaultDesignConfig.tabTop, ...(p.tabTop || {}) }
            } as SimpleDesignConfig;
        } catch {
            return { ...defaultDesignConfig };
        }
    }

    // Konfiguration der aktuell gewählten Quelle (oder null)
    getSelectedConfig = (): SimpleDesignConfig | null => {
        const { quelleTyp, quelleId } = this.state;
        if (quelleTyp === null || quelleId === null) return null;
        if (quelleTyp === 'version') {
            const v = this.props.versions.find(x => x.version === quelleId);
            return v ? this.parseConfig(v.konfigurationJson) : null;
        }
        const t = this.props.templates.find(x => x.id === quelleId);
        return t ? this.parseConfig(t.konfigurationJson) : null;
    }

    executeTransfer = async () => {
        const { quelleTyp, quelleId, zielKunde, zielGemeinden } = this.state;
        if (quelleTyp === null || quelleId === null || zielKunde === null || zielGemeinden.length === 0) {
            return;
        }

        this.setState({ isTransferring: true, error: null });
        try {
            const api = env.REACT_APP_KSLPLUS_API || "https://localhost:5001";
            const request = {
                quelleKunde: this.props.kunde,
                quelleGemeinde: this.props.gemeinde,
                quelleJahr: this.props.jahr,
                quelleVersion: quelleTyp === 'version' ? quelleId : null,
                quelleTemplateId: quelleTyp === 'template' ? quelleId : null,
                zielKunde: zielKunde,
                zielGemeinden: zielGemeinden
            };
            const response = await fetch(`${api}/api/Design/transfer-extern`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': this.props.authToken
                },
                body: JSON.stringify(request)
            });

            if (!response.ok) {
                let message = `HTTP ${response.status}`;
                if (response.status === 401 || response.status === 403) {
                    message = 'Keine Berechtigung für die kundenübergreifende Übertragung.';
                } else {
                    try {
                        const e = await response.json();
                        if (e && e.error) message = e.error;
                    } catch {
                        // Antwort ohne JSON-Body
                    }
                }
                throw new Error(message);
            }

            const result = await response.json();
            const anzahl = (result && typeof result.uebertragen === 'number')
                ? result.uebertragen
                : zielGemeinden.length;

            this.setState({ isTransferring: false });
            this.props.onSuccess(anzahl);
        } catch (error) {
            this.setState({
                isTransferring: false,
                error: 'Fehler beim Übertragen: ' + (error as Error).message
            });
        }
    }

    // Header-Hintergrund (einfarbig oder Verlauf) aus der Konfiguration
    getHeaderBackground = (cfg: SimpleDesignConfig): string => {
        if (cfg.headerGradientEnabled) {
            return `linear-gradient(${cfg.headerGradientAngle}deg, ${cfg.headerBgColor}, ${cfg.headerGradientColor})`;
        }
        if (cfg.headerGradientToWhite) {
            return `linear-gradient(to right, ${cfg.headerBgColor}, #ffffff, ${cfg.headerBgColor})`;
        }
        return cfg.headerBgColor;
    }

    /**
     * Mini-Vorschau des Designlooks (Header, Reiter, Inhalt, Schaltfläche).
     * Hinweis: für dynamische Textfarben werden <b>-Elemente genutzt – die
     * Farb-Isolation des Designers zwingt <div>/<span> auf color:inherit.
     */
    renderMiniPreview = (cfg: SimpleDesignConfig) => {
        const headingWeight = cfg.headingBold ? 700 : 400;
        return (
            <div className="dcsa-pv-frame">
                <div className="dcsa-pv-header" style={{ background: this.getHeaderBackground(cfg) }}>
                    <b className="dcsa-pv-headline" style={{ color: cfg.headlineMainColor, fontWeight: headingWeight }}>
                        Musterstadt
                    </b>
                </div>
                <div className="dcsa-pv-tabs" style={{ background: cfg.websiteBackgroundColor }}>
                    <b className="dcsa-pv-tab" style={{ background: cfg.tabTop.activeBgColor, color: cfg.tabTop.activeColor }}>
                        Haushalt
                    </b>
                    <b className="dcsa-pv-tab" style={{ background: cfg.tabTop.inactiveBgColor, color: cfg.tabTop.inactiveColor }}>
                        Finanzen
                    </b>
                    <b className="dcsa-pv-tab" style={{ background: cfg.tabTop.inactiveBgColor, color: cfg.tabTop.inactiveColor }}>
                        Vergleich
                    </b>
                </div>
                <div className="dcsa-pv-content" style={{ background: cfg.websiteBackgroundColor }}>
                    <b className="dcsa-pv-heading" style={{ color: cfg.headingColor, fontWeight: headingWeight }}>
                        Überschrift
                    </b>
                    <div className="dcsa-pv-textline" />
                    <div className="dcsa-pv-textline dcsa-pv-textline-short" />
                    <button
                        className="dcsa-pv-btn"
                        type="button"
                        tabIndex={-1}
                        style={{ background: cfg.buttonBgColor, color: cfg.buttonTextColor }}
                    >
                        Schaltfläche
                    </button>
                </div>
            </div>
        );
    }

    // Linke Spalte: Auswahl der Quelle (gespeicherte Versionen + Vorlagen)
    renderQuelleSpalte = () => {
        const { versions, templates } = this.props;
        const { quelleTyp, quelleId } = this.state;

        return (
            <div className="dcsa-col dcsa-col-source">
                <div className="dcsa-col-title">1 · Quelle wählen</div>

                <div className="dcsa-group-label">Gespeicherte Versionen</div>
                {versions.length === 0 ? (
                    <div className="dcsa-empty">Keine Versionen vorhanden.</div>
                ) : (
                    <div className="dcsa-quelle-list">
                        {versions.map(v => {
                            const sel = quelleTyp === 'version' && quelleId === v.version;
                            return (
                                <button
                                    key={`v${v.version}`}
                                    type="button"
                                    className={`dcsa-quelle-item ${sel ? 'selected' : ''}`}
                                    onClick={() => this.selectQuelle('version', v.version)}
                                >
                                    <span className="dcsa-radio" />
                                    <span className="dcsa-quelle-text">
                                        <span className="dcsa-quelle-row">
                                            <span className="dcsa-quelle-name">Version {v.version}</span>
                                            {v.istLive && <span className="dcsa-live-badge">Live</span>}
                                        </span>
                                        {v.erstelltAm && (
                                            <span className="dcsa-quelle-date">
                                                Erstellt: {this.formatTimestamp(v.erstelltAm)}
                                            </span>
                                        )}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}

                <div className="dcsa-group-label">Vorlagen</div>
                {templates.length === 0 ? (
                    <div className="dcsa-empty">Keine Vorlagen verfügbar.</div>
                ) : (
                    <div className="dcsa-quelle-list">
                        {templates.map(t => {
                            const sel = quelleTyp === 'template' && quelleId === t.id;
                            return (
                                <button
                                    key={`t${t.id}`}
                                    type="button"
                                    className={`dcsa-quelle-item ${sel ? 'selected' : ''}`}
                                    onClick={() => this.selectQuelle('template', t.id)}
                                >
                                    <span className="dcsa-radio" />
                                    <span className="dcsa-quelle-text">
                                        <span className="dcsa-quelle-row">
                                            <span className="dcsa-quelle-name">{t.name}</span>
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    // Mittlere Spalte: dauerhaft sichtbare Vorschau des gewählten Designs
    renderVorschauSpalte = () => {
        const selectedConfig = this.getSelectedConfig();
        return (
            <div className="dcsa-col dcsa-col-preview">
                <div className="dcsa-col-title">Vorschau</div>
                {selectedConfig ? (
                    this.renderMiniPreview(selectedConfig)
                ) : (
                    <div className="dcsa-preview-empty">
                        Links eine Quelle wählen, um die Vorschau zu sehen.
                    </div>
                )}
            </div>
        );
    }

    // Rechte Spalte: Ziel-Kunde und dessen Gemeinden
    renderZielSpalte = () => {
        const { allKunden, isLoadingKunden, zielKunde, zielGemeinden } = this.state;
        const selectedKunde = allKunden.find(k => k.kunde === zielKunde) || null;

        return (
            <div className="dcsa-col dcsa-col-target">
                <div className="dcsa-col-title">2 · Ziel-Kunde &amp; Gemeinden</div>

                {isLoadingKunden ? (
                    <div className="dcsa-loading"><span className="dcsa-spinner-sm" /></div>
                ) : allKunden.length === 0 ? (
                    <div className="dcsa-empty">Keine Kunden verfügbar.</div>
                ) : (
                    <>
                        <select
                            className="dcsa-select"
                            value={zielKunde ?? ''}
                            onChange={(e) => this.selectZielKunde(e.target.value ? parseInt(e.target.value) : null)}
                        >
                            <option value="">– Kunde wählen –</option>
                            {allKunden.map(k => (
                                <option key={k.kunde} value={k.kunde}>{k.name}</option>
                            ))}
                        </select>

                        {selectedKunde && (
                            selectedKunde.gemeinden.length === 0 ? (
                                <div className="dcsa-empty">Dieser Kunde hat keine Gemeinden.</div>
                            ) : (
                                <div className="dcsa-gemeinde-list">
                                    {selectedKunde.gemeinden.map(g => (
                                        <label key={g.gemeinde} className="dcsa-gemeinde-item">
                                            <input
                                                type="checkbox"
                                                checked={zielGemeinden.includes(g.gemeinde)}
                                                onChange={() => this.toggleZielGemeinde(g.gemeinde)}
                                            />
                                            <span className="dcsa-gemeinde-name">{g.name}</span>
                                        </label>
                                    ))}
                                </div>
                            )
                        )}
                    </>
                )}
            </div>
        );
    }

    render() {
        const { quelleTyp, quelleId, zielKunde, zielGemeinden, isTransferring, error } = this.state;

        const kannUebertragen = quelleTyp !== null && quelleId !== null
            && zielKunde !== null && zielGemeinden.length > 0 && !isTransferring;

        return (
            <div className="dcsa-overlay">
                <div className="dcsa-dialog">
                    {/* Kopfbereich */}
                    <div className="dcsa-header">
                        <div className="dcsa-header-title">
                            <FiUsers className="dcsa-header-icon" size={20} />
                            <div>
                                <div className="dcsa-title">Design an anderen Kunden übertragen</div>
                                <span className="dcsa-subtitle">Superadmin · kundenübergreifend</span>
                            </div>
                        </div>
                        <button className="dcsa-close" onClick={this.props.onClose} type="button" disabled={isTransferring}>
                            <FiX size={20} />
                        </button>
                    </div>

                    {/* Inhalt: drei Spalten – Quelle, Vorschau, Ziel */}
                    <div className="dcsa-body">
                        {this.renderQuelleSpalte()}
                        {this.renderVorschauSpalte()}
                        {this.renderZielSpalte()}
                    </div>

                    {error && (
                        <div className="dcsa-error">
                            <FiAlertCircle size={15} />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Fußbereich */}
                    <div className="dcsa-footer">
                        <div className="dcsa-footer-hint">
                            <FiInfo size={14} />
                            <span>Wird beim Ziel als neue Live-Version gespeichert; der Kunde kann es danach weiter anpassen.</span>
                        </div>
                        <div className="dcsa-footer-actions">
                            <button
                                className="dcsa-btn dcsa-btn-cancel"
                                onClick={this.props.onClose}
                                type="button"
                                disabled={isTransferring}
                            >
                                Abbrechen
                            </button>
                            <button
                                className="dcsa-btn dcsa-btn-confirm"
                                onClick={this.executeTransfer}
                                type="button"
                                disabled={!kannUebertragen}
                            >
                                {isTransferring ? <span className="dcsa-spinner-sm" /> : <FiShare2 size={15} />}
                                <span>Übertragen &amp; live schalten</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default DesignConfiguratorSA;
